<template lang="pug">
  div
    b-row
      .col-xl-12
        panel(:title='$t("ui.partner_list")')
          b-card.partner-board
            b-row.flex(slot="header")
              .col-sm-12.col-md-12.col-lg-12.col-xl-12.mb-1
                b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ $t(submission.status) }}
              .col-sm-12.col-md-6.col-lg-6.col-xl-2
                SubmitButton(:loading="loading" icon="fa fa-sync" :text="$t('ui.refresh')" isModal=false type="button" @click="refreshAll")

            b-alert.partner-priority-alert.mb-3(
              show
              variant="info"
            )
              i.fa.fa-info-circle.mr-1
              span {{ $t("message.partner_ldiff_margin_priority") }}

            //- Sports Type
            .partner-section
              .partner-section-header
                .partner-section-title
                  h5.mb-0 {{ $t("ui.sports_type") }}
                  span.partner-count-badge {{ filteredSports.length }}/{{ partnerList.length }}
                .partner-section-actions
                  b-form-input.partner-search(
                    size="sm"
                    type="search"
                    v-model.trim="sportsSearch"
                    :placeholder="$t('ui.search')"
                  )

              .partner-grid-wrap(v-if="!loadingSports")
                .partner-empty.text-muted(v-if="!filteredSports.length") {{ $t("message.no_records") }}
                .partner-sports-grid(v-else)
                  .partner-sport-card(
                    v-for="item in filteredSports"
                    :key="'sport-' + item.sports_type"
                    :class="{ active: isSportSelected(item) }"
                    role="button"
                    tabindex="0"
                    :title="$t('ui.partner_sport_card_hint')"
                    @click="selectSport(item)"
                    @dblclick="onSportCardEdit(item)"
                    @keyup.enter="selectSport(item)"
                  )
                    .partner-sport-card-main
                      .partner-sport-name {{ displaySportType(item.name_en || item.sports_type) }}
                      .partner-metrics
                        .partner-metric
                          span.partner-metric-label {{ $t("ui.ldiff") }}
                          span.partner-metric-value {{ formatLdiff(item.ldiff) }}
                        .partner-metric
                          span.partner-metric-label {{ $t("ui.margin") }}
                          span.partner-metric-value {{ formatMargin(item.margin) }}
              .partner-loading(v-else)
                b-spinner.text-primary(small)
                span.ml-2 {{ $t("ui.refresh") }}...

            //- Market + Bet Type scoped to selected sport
            .partner-context(v-if="selectedSportsType !== ''")
              .partner-context-bar
                .partner-context-copy
                  span.partner-context-kicker {{ $t("ui.partner_showing_for") }}
                  span.partner-context-sport {{ selectedSportLabel }}
                  span.partner-context-hint {{ $t("ui.partner_showing_for_hint") }}
                .partner-context-aside
                  .partner-context-metrics(v-if="selectedItem && selectedItem.sports_type")
                    .partner-metric
                      span.partner-metric-label {{ $t("ui.ldiff") }}
                      span.partner-metric-value {{ formatLdiff(selectedItem.ldiff) }}
                    .partner-metric
                      span.partner-metric-label {{ $t("ui.margin") }}
                      span.partner-metric-value {{ formatMargin(selectedItem.margin) }}
                  b-button(
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    @click="onSportCardEdit(selectedItem)"
                  )
                    i.fa.fa-pencil-alt.mr-1
                    | {{ $t("ui.edit") }}

              .partner-context-body
                //- Market Type
                .partner-section.partner-section--nested
                  .partner-section-header
                    .partner-section-title
                      h5.mb-0 {{ $t("ui.market_type") }}
                      span.partner-count-badge.partner-count-badge--muted {{ selectedMarkets.length }}/{{ selectedMarkets.length }}
                  .partner-market-strip(v-if="!loadingMarkets")
                    .partner-empty.text-muted(v-if="!selectedMarkets.length") {{ $t("message.no_records") }}
                    .partner-market-grid(v-else)
                      button.partner-market-card(
                        type="button"
                        v-for="item in selectedMarkets"
                        :key="'market-' + item.market_type"
                        v-b-modal.upsertPartnerMarket
                        @click="openUpsertMarket(item)"
                      )
                        .partner-market-head
                          span.partner-market-bar(:class="marketBarClass(item.market_type)")
                          span.partner-market-name {{ displayMarketType(item.market_type) }}
                        .partner-metrics
                          .partner-metric
                            span.partner-metric-label {{ $t("ui.ldiff") }}
                            span.partner-metric-value {{ formatLdiff(item.ldiff) }}
                          .partner-metric
                            span.partner-metric-label {{ $t("ui.margin") }}
                            span.partner-metric-value {{ formatMargin(item.margin) }}
                  .partner-loading(v-else)
                    b-spinner.text-primary(small)
                    span.ml-2 {{ $t("ui.refresh") }}...

                //- Bet Type
                .partner-section.partner-section--nested.mb-0
                  .partner-section-header
                    .partner-section-title
                      h5.mb-0 {{ $t("ui.bet_type") }}
                      span.partner-count-badge.partner-count-badge--muted {{ filteredBetTypeGroups.length }}/{{ betTypeGroups.length }}
                    .partner-section-actions
                      b-form-input.partner-search(
                        size="sm"
                        type="search"
                        v-model.trim="betTypeSearch"
                        :placeholder="$t('ui.search')"
                      )
                  .partner-grid-wrap.partner-grid-wrap--bet(v-if="!loadingBetTypes")
                    .partner-empty.text-muted(v-if="!filteredBetTypeGroups.length") {{ $t("message.no_records") }}
                    .partner-bet-grid(v-else)
                      .partner-bet-card(v-for="group in filteredBetTypeGroups" :key="'bet-' + group.key")
                        .partner-bet-card-title {{ group.label }}
                        button.partner-bet-row(
                          type="button"
                          v-for="row in group.rows"
                          :key="group.key + '-' + row.market_type"
                          v-b-modal.upsertPartnerBetType
                          @click="openUpsertBetType(row)"
                        )
                          span.partner-bet-bar(:class="marketBarClass(row.market_type)")
                          span.partner-bet-row-label {{ displayMarketType(row.market_type) }}
                          .partner-metrics.partner-metrics--inline
                            .partner-metric
                              span.partner-metric-label {{ $t("ui.ldiff") }}
                              span.partner-metric-value {{ formatLdiff(row.ldiff) }}
                            .partner-metric
                              span.partner-metric-label {{ $t("ui.margin") }}
                              span.partner-metric-value {{ formatMargin(row.margin) }}
                  .partner-loading(v-else)
                    b-spinner.text-primary(small)
                    span.ml-2 {{ $t("ui.refresh") }}...

      upsertPartnerSports(:selected="selectedItem" @clicked="onSportsUpdated")
      upsertPartnerMarket(:selected="selectedMarketItem" @clicked="onMarketUpdated")
      upsertPartnerBetType(:selected="selectedBetTypeItem" @clicked="onBetTypeUpdated")
</template>

<script>
import SubmitButton from "@/library/SubmitButton.vue";
import upsertPartnerSports from "@/components/modal/upsertPartnerSports.vue";
import upsertPartnerMarket from "@/components/modal/upsertPartnerMarket.vue";
import upsertPartnerBetType from "@/components/modal/upsertPartnerBetType.vue";

export default {
  components: {
    SubmitButton,
    upsertPartnerSports,
    upsertPartnerMarket,
    upsertPartnerBetType
  },
  data() {
    return {
      loading: false,
      loadingSports: false,
      loadingMarkets: false,
      loadingBetTypes: false,
      submission: {
        success: true,
        status: ""
      },
      partnerList: [],
      marketList: [],
      betTypeList: [],
      selectedItem: {},
      selectedMarketItem: {},
      selectedBetTypeItem: {},
      selectedSportsType: "",
      sportsSearch: "",
      betTypeSearch: ""
    };
  },
  computed: {
    selectedSportLabel() {
      if (!this.selectedItem || !this.selectedItem.sports_type) {
        return "";
      }
      return this.displaySportType(
        this.selectedItem.name_en || this.selectedItem.sports_type
      );
    },
    filteredSports() {
      const q = String(this.sportsSearch || "")
        .trim()
        .toLowerCase();
      if (!q) {
        return this.partnerList;
      }
      return this.partnerList.filter(item => {
        const name = String(item.name_en || item.sports_type || "").toLowerCase();
        const type = String(item.sports_type || "").toLowerCase();
        return name.indexOf(q) !== -1 || type.indexOf(q) !== -1;
      });
    },
    selectedMarkets() {
      const selectedSportsType = String(this.selectedSportsType);
      return (this.marketList || [])
        .filter(item => {
          return (
            item &&
            item.sports_type !== undefined &&
            item.sports_type !== null &&
            String(item.sports_type) === selectedSportsType
          );
        })
        .slice()
        .sort((a, b) => Number(a.market_type) - Number(b.market_type));
    },
    betTypeGroups() {
      const selectedSportsType = String(this.selectedSportsType);
      const map = {};
      (this.betTypeList || []).forEach(item => {
        if (
          !item ||
          item.sports_type === undefined ||
          item.sports_type === null ||
          String(item.sports_type) !== selectedSportsType
        ) {
          return;
        }
        const key = String(
          item.bet_type !== undefined && item.bet_type !== null && item.bet_type !== ""
            ? item.bet_type
            : item.name_en || item.bet_type_name || ""
        );
        if (!key) {
          return;
        }
        if (!map[key]) {
          map[key] = {
            key,
            label: this.displayBetType(item),
            rows: []
          };
        }
        map[key].rows.push(item);
      });
      return Object.keys(map)
        .map(key => {
          const group = map[key];
          group.rows = group.rows
            .slice()
            .sort((a, b) => Number(a.market_type) - Number(b.market_type));
          return group;
        })
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    },
    filteredBetTypeGroups() {
      const q = String(this.betTypeSearch || "")
        .trim()
        .toLowerCase();
      if (!q) {
        return this.betTypeGroups;
      }
      return this.betTypeGroups.filter(group => {
        return String(group.label || "")
          .toLowerCase()
          .indexOf(q) !== -1;
      });
    }
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    displaySportType(value) {
      return String(value || "").toUpperCase();
    },
    displayMarketType(marketType) {
      const type = Number(marketType);
      if (type === 1) {
        return this.$t("ui.early").toUpperCase();
      }
      if (type === 2) {
        return this.$t("ui.today").toUpperCase();
      }
      if (type === 3) {
        return this.$t("ui.live").toUpperCase();
      }
      return "";
    },
    displayBetType(item) {
      return String(
        item.name_en || item.bet_type_name || item.bet_type || ""
      ).toUpperCase();
    },
    marketBarClass(marketType) {
      const type = Number(marketType);
      if (type === 1) {
        return "is-early";
      }
      if (type === 2) {
        return "is-today";
      }
      if (type === 3) {
        return "is-live";
      }
      return "";
    },
    formatLdiff(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return value;
      }
      return num.toFixed(1);
    },
    formatMargin(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return value;
      }
      return String(Math.round(num));
    },
    isSportSelected(item) {
      return (
        item &&
        String(item.sports_type) === String(this.selectedSportsType) &&
        this.selectedSportsType !== ""
      );
    },
    selectSport(item) {
      if (!item) {
        return;
      }
      this.selectedItem = Object.assign({}, item);
      this.selectedSportsType =
        item.sports_type !== undefined && item.sports_type !== null
          ? item.sports_type
          : "";
      this.$store.dispatch("user/setSelectedSportsType", this.selectedSportsType);
      this.$store.dispatch(
        "user/setSelectedSportsName",
        item.name_en || ""
      );
      this.betTypeSearch = "";
    },
    onSportCardEdit(item) {
      this.selectSport(item);
      this.openUpsertSports(item);
      this.$nextTick(() => {
        this.$bvModal.show("upsertPartnerSports");
      });
    },
    openUpsertSports(item) {
      if (!item) {
        return;
      }
      this.selectedItem = Object.assign({}, item);
    },
    openUpsertMarket(item) {
      this.selectedMarketItem = Object.assign({}, item, {
        sports_type:
          item.sports_type !== undefined && item.sports_type !== null
            ? item.sports_type
            : this.selectedSportsType,
        sports_name:
          (this.selectedItem && this.selectedItem.name_en) ||
          this.$store.getters.getSelectedSportsName ||
          ""
      });
    },
    openUpsertBetType(item) {
      this.selectedBetTypeItem = Object.assign({}, item, {
        sports_type:
          item.sports_type !== undefined && item.sports_type !== null
            ? item.sports_type
            : this.selectedSportsType,
        sports_name:
          (this.selectedItem && this.selectedItem.name_en) ||
          this.$store.getters.getSelectedSportsName ||
          ""
      });
    },
    onSportsUpdated() {
      this.loadSports(true);
      this.loadMarkets();
      this.loadBetTypes();
    },
    onMarketUpdated() {
      this.loadMarkets();
      this.loadBetTypes();
    },
    onBetTypeUpdated() {
      this.loadBetTypes();
    },
    refreshAll() {
      this.loadSports(true);
      this.loadMarkets();
      this.loadBetTypes();
    },
    loadSports(preserveSelection) {
      this.loading = true;
      this.loadingSports = true;
      this.$store
        .dispatch("user/getPartnerSettingSports")
        .then(response => {
          this.loading = false;
          this.loadingSports = false;
          this.submission.success = response.success;
          this.submission.status = response.errorTag;
          if (response.success == true) {
            this.partnerList = response.data || [];
            this.ensureSportSelection(preserveSelection);
          } else {
            this.partnerList = [];
            this.clearSportSelection();
          }
        })
        .catch(response => {
          this.loading = false;
          this.loadingSports = false;
          this.partnerList = [];
          this.clearSportSelection();
          this.submission.success = response.success;
          this.submission.status = response.errorTag;
        });
    },
    ensureSportSelection(preserveSelection) {
      if (!this.partnerList.length) {
        this.clearSportSelection();
        return;
      }
      let next = null;
      if (preserveSelection && this.selectedSportsType !== "") {
        next = this.partnerList.find(
          item => String(item.sports_type) === String(this.selectedSportsType)
        );
      }
      if (!next) {
        const stored = this.$store.getters.getSelectedSportsType;
        if (stored !== undefined && stored !== null && stored !== "") {
          next = this.partnerList.find(
            item => String(item.sports_type) === String(stored)
          );
        }
      }
      if (!next) {
        next = this.partnerList[0];
      }
      this.selectSport(next);
    },
    clearSportSelection() {
      this.selectedItem = {};
      this.selectedSportsType = "";
      this.$store.dispatch("user/setSelectedSportsType", "");
      this.$store.dispatch("user/setSelectedSportsName", "");
    },
    loadMarkets() {
      this.loadingMarkets = true;
      this.$store
        .dispatch("user/getPartnerSettingMarket")
        .then(response => {
          this.loadingMarkets = false;
          if (response.success == true) {
            this.marketList = response.data || [];
          } else {
            this.marketList = [];
            if (!this.submission.success) {
              return;
            }
            this.submission.success = response.success;
            this.submission.status = response.errorTag;
          }
        })
        .catch(response => {
          this.loadingMarkets = false;
          this.marketList = [];
          this.submission.success = response.success;
          this.submission.status = response.errorTag;
        });
    },
    loadBetTypes() {
      this.loadingBetTypes = true;
      this.$store
        .dispatch("user/getPartnerSettingBetType")
        .then(response => {
          this.loadingBetTypes = false;
          if (response.success == true) {
            this.betTypeList = response.data || [];
          } else {
            this.betTypeList = [];
            if (!this.submission.success) {
              return;
            }
            this.submission.success = response.success;
            this.submission.status = response.errorTag;
          }
        })
        .catch(response => {
          this.loadingBetTypes = false;
          this.betTypeList = [];
          this.submission.success = response.success;
          this.submission.status = response.errorTag;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.partner-board {
  background: #f4f6f7;
}

.partner-priority-alert {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
}

.partner-section {
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  &--nested {
    margin-bottom: 1rem;
  }
}

.partner-context {
  margin-top: 0.25rem;
  background: #fff;
  border: 1px solid #d0dbe8;
  border-radius: 4px;
  border-left: 4px solid #348fe2;
  box-shadow: 0 1px 2px rgba(45, 53, 60, 0.04);
  overflow: hidden;
}

.partner-context-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(90deg, #eaf4fc 0%, #f7fafc 100%);
  border-bottom: 1px solid #d9e6f2;
}

.partner-context-copy {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.55rem;
  min-width: 0;
}

.partner-context-kicker {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c7a89;
}

.partner-context-sport {
  font-size: 1rem;
  font-weight: 700;
  color: #348fe2;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.partner-context-hint {
  width: 100%;
  font-size: 0.75rem;
  color: #8895a7;
}

.partner-context-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(4.5rem, auto));
  gap: 0.35rem;
}

.partner-context-aside {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.partner-context-body {
  padding: 1rem;
  background: #f8fafb;
}

.partner-section-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.partner-section-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  h5 {
    font-weight: 700;
    color: #2d353c;
  }
}

.partner-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 1.5rem;
  padding: 0 0.5rem;
  border-radius: 999px;
  background: #348fe2;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;

  &--muted {
    background: #6c757d;
  }
}

.partner-selected-label {
  color: #348fe2;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.partner-section-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.partner-search {
  width: 180px;
  max-width: 100%;
}

.partner-grid-wrap,
.partner-market-strip {
  background: #fff;
  border: 1px solid #d9e0e7;
  border-radius: 4px;
  padding: 0.75rem;
}

.partner-grid-wrap--bet {
  max-height: 420px;
  overflow: auto;
}

.partner-sports-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  max-height: 280px;
  overflow: auto;
}

.partner-sport-card {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #cfd8e3;
  border-radius: 4px;
  padding: 0.65rem 0.55rem 0.65rem 0.75rem;
  color: #2d353c;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
  cursor: pointer;

  &:hover {
    border-color: #348fe2;
  }

  &.active {
    background: #348fe2;
    border-color: #348fe2;
    color: #fff;

    .partner-metric {
      background: rgba(255, 255, 255, 0.16);
      border-color: rgba(255, 255, 255, 0.22);
    }

    .partner-metric-label {
      color: rgba(255, 255, 255, 0.78);
    }

    .partner-metric-value {
      color: #fff;
    }
  }
}

.partner-sport-card-main {
  min-width: 0;
  flex: 1 1 auto;
}

.partner-sport-name {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
  word-break: break-word;
  margin-bottom: 0.45rem;
}

.partner-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem;
}

.partner-metrics--inline {
  margin-left: auto;
  flex: 0 1 auto;
  min-width: 9.5rem;
  max-width: 12rem;
}

.partner-metric {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  padding: 0.28rem 0.4rem;
  background: #f4f7fa;
  border: 1px solid #e4ebf2;
  border-radius: 3px;
}

.partner-metric-label {
  display: block;
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8895a7;
}

.partner-metric-value {
  display: block;
  margin-top: 0.12rem;
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.15;
  color: #2d353c;
  font-variant-numeric: tabular-nums;
}

.partner-market-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
}

.partner-market-card {
  display: block;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #cfd8e3;
  border-radius: 4px;
  padding: 0.7rem 0.8rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #348fe2;
    box-shadow: 0 1px 4px rgba(52, 143, 226, 0.12);
  }

  .partner-metrics {
    margin-top: 0.5rem;
  }
}

.partner-market-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.partner-market-name,
.partner-bet-card-title,
.partner-bet-row-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #2d353c;
  letter-spacing: 0.02em;
}

.partner-market-bar,
.partner-bet-bar {
  width: 3px;
  height: 1rem;
  border-radius: 2px;
  background: #adb5bd;
  flex: 0 0 auto;

  &.is-early {
    background: #00acac;
  }

  &.is-today {
    background: #348fe2;
  }

  &.is-live {
    background: #ff5b57;
  }
}

.partner-bet-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
}

.partner-bet-card {
  background: #fff;
  border: 1px solid #cfd8e3;
  border-radius: 4px;
  padding: 0.7rem 0.75rem;
}

.partner-bet-card-title {
  margin-bottom: 0.45rem;
}

.partner-bet-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4rem 0.2rem;
  border: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 3px;

  &:hover {
    background: #f2f6fa;
  }

  & + .partner-bet-row {
    border-top: 1px solid #eef1f4;
  }
}

.partner-bet-row-label {
  flex: 0 0 auto;
  min-width: 3.5rem;
  font-size: 0.75rem;
}

.partner-empty,
.partner-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  font-size: 0.85rem;
}

@media (max-width: 1199.98px) {
  .partner-sports-grid,
  .partner-bet-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991.98px) {
  .partner-sports-grid,
  .partner-bet-grid,
  .partner-market-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .partner-sports-grid,
  .partner-bet-grid,
  .partner-market-grid {
    grid-template-columns: 1fr;
  }

  .partner-search {
    width: 100%;
  }

  .partner-section-actions {
    width: 100%;
  }

  .partner-metrics--inline {
    min-width: 0;
    max-width: none;
    flex: 1 1 auto;
  }
}
</style>
